/* ============================================================
   PRO STUDIO  —  SESAAT Studio v3.9
   ============================================================
   Fail ini berkhidmat kepada DUA laluan, dikawal oleh `modSebenar`
   — corak yang sama seperti MarketplaceV39View:

     modSebenar = false  →  /prostudio-v3.9   (PELAN reka bentuk)
       Data contoh sepenuhnya. Petak angka memapar keadaan siap
       yang dituju, supaya rupa akhir boleh dinilai.

     modSebenar = true   →  /prostudio        (HALAMAN HIDUP)
       Muncul hanya bila suis `config/ui_v39` hidup; kalau tidak
       App.jsx memapar ProStudioView lama. Dalam mod ini:
         · Agent Saya memanggil ProStudioAgentSection yang SEBENAR
           (Firestore + jambatan extension) — bukan rangka statik.
         · Gaya dan Hook Bank kekal bertanda "Akan datang" kerana
           kedua-duanya memang belum bersambung ke Firestore.
           ⛔ Jangan buang tanda itu sebelum simpanan sebenar siap
              — kawalan yang nampak berfungsi tetapi tidak menyimpan
              apa-apa ialah tepat jenis kawalan palsu yang dibuang
              daripada halaman Settings pada 10 Ogos.
         · `readOnly` (bukan admin) melumpuhkan semua kawalan, sama
           seperti halaman lama.

   ⛔ Halaman lama ProStudioView.jsx TIDAK dipadam. Tutup suis v3.9
      dan ia kembali serta-merta, tanpa deploy.

   ⚠️ Alamat lama /pro-studio kekal hidup sebagai lencongan ke
      /prostudio (App.jsx) — pelanggan sudah ada pautan ke situ.

   ⭐ PERUBAHAN LAYOUT (diluluskan user 2026-08-16) — DUA TINGKAT,
      bukan tab bertindan.

      Halaman lama menyusun lima lapisan menu di atas satu skrin:
        rel app → tab seksyen 190px → pil module → senarai template
        225px → editor → kolum set.
      Editor tinggal ~560px, jadi tiga kolum set berhimpit dan
      berpatah pada skrin komputer riba biasa.

      Susunan baharu:
        Tingkat 1 (hub)  — petak angka + senarai 6 seksyen. Satu
                           pandangan terus tahu apa yang sedia dan
                           apa yang belum. TIADA tab.
        Tingkat 2 (fokus)— satu seksyen ambil alih seluruh panel,
                           dengan pautan "← PRO Studio" di atas.
                           Editor dapat lebar penuh panel.

   Peraturan rupa yang dipatuhi (sama seperti halaman v3.9 lain):
     · Warna SEMUA daripada token --s-* — tiada kod warna sendiri,
       jadi tema gelap/terang ikut serentak.
     · Monokrom. Tiada emoji, tiada gradien. Warna status (--s-ok/
       warn/stop) hanya untuk maksud status, bukan hiasan.
     · Saiz teks terkecil 11px. Halaman lama turun sampai 7.5px.
   ============================================================ */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Palette, Bot, Anchor, Hash, PenLine, Bookmark, Boxes, FlaskConical,
    ChevronRight, ArrowLeft, Star, Trash2, Copy, Plus, Shuffle,
    Lock, Info, Check, X, Sparkles, Slash,
} from 'lucide-react';

import '../design/tokens.css';
import { Badge, Btn, IconBtn, PageHeader, Segmented, StatTile, List, Row, EmptyState } from '../design/ui';
import { klikBiasa } from '../utils/klikBiasa';
/* Blok binaan PRO Studio — dikongsi dengan stepper Agent. Lihat nota
   dalam fail itu tentang kenapa ia tidak duduk dalam fail ini. */
import { Lbl, Inp, Ta, Sel, Cip, Nota, Kad } from './prostudioUI';
/* Lakaran A — stepper berpandu untuk Agent Saya. MOD PELAN SAHAJA:
   data contoh, tiada Firestore. Mod sebenar kekal memanggil
   ProStudioAgentSection yang asal. */
import ProStudioAgentStepperV39 from './ProStudioAgentStepperV39';
/* Senarai agent — komponen yang SAMA dipakai di dua tempat: panel SMART
   pada hub, dan halaman senarai penuh. Satu takrifan, supaya dua senarai
   itu tidak boleh terpesong antara satu sama lain. */
import SenaraiAgentV39 from './SenaraiAgentV39';
import { agentContoh } from './prostudioAgents';
/* Agent SEBENAR untuk hub (mod sebenar sahaja). Bacaan ringkas —
   lihat nota dalam fail hook. */
import { useSenaraiAgent } from '../hooks/useSenaraiAgent';
import { useSenaraiCta } from '../hooks/useSenaraiCta';
import ProStudioCtaSection from './ProStudioCtaSection';
/* PROTOTAIP blocks (module GOYANG) — mod pelan sahaja. RUPA SAHAJA,
   state lokal, tiada Firestore. Lihat nota kepala fail itu. */
import ProStudioBlokSection from './ProStudioBlokSection';
import { simpanCta, padamCta, bersihkanCta } from '../utils/ctaStore';
import { useSenaraiShortcut } from '../hooks/useSenaraiShortcut';
import ProStudioShortcutSection from './ProStudioShortcutSection';
import { simpanShortcut, padamShortcut, bersihkanNama, bersihkanTeks, semakShortcut } from '../utils/shortcutStore';
import { useCiriPercuma } from '../hooks/useCiriPercuma';
import PopupCiriPercuma from './PopupCiriPercuma';
import { CIRI, bolehGunaCiri, nantiHariIni } from '../utils/ciriPercuma';
/* Susunan agent pilihan pelanggan — dikongsi dengan senarai penuh dalam
   halaman Agent dan pemilih agent di Workspace. */
import { simpanSusunanAgent, padamAgent as padamAgentDoc, tetapAgentDiWorkspace } from '../utils/susunAgent';
import { auth } from '../firebase';
import { useT } from '../i18n';
/* Seksyen Agent yang SEBENAR (Firestore + extension). Dipakai hanya
   dalam mod sebenar — lihat nota di kepala fail. Prop `v39` menukar
   paletnya kepada token --s-*; tanpa prop itu ia kekal rupa lama untuk
   halaman lama. Logiknya tidak disentuh langsung. */
import ProStudioAgentSection from './ProStudioAgentSection';

/* ── Data contoh ──────────────────────────────────────────────── */

/* ⚠️ NILAI DATA, bukan label. `AUTO` disimpan ke dalam keadaan dan
   dibandingkan dengan `===` di beberapa tempat, jadi ia KEKAL rentetan
   tetap. Hanya paparannya diterjemah (lihat `medanGaya`). Perkara yang
   sama terpakai pada senarai `pilihan` di bawah. */
const AUTO = 'Auto (AI tentukan)';

/* ⭐ Pemalar aras-modul TIDAK boleh memanggil useT(), jadi `label`,
   `tajuk`, `sub` dan `perihal` di bawah menyimpan KUNCI kamus —
   terjemahan berlaku pada masa render. Corak yang sama seperti
   HALAMAN_SAH dalam SettingsV39View. */
const MEDAN = [
    { key: 'sudut', label: 'prostudio.style.field.angle', pilihan: [AUTO, 'High Angle', 'Eye Level', 'Low Angle'] },
    { key: 'lokasi', label: 'prostudio.style.field.location', pilihan: [AUTO, 'Bilik Moden', 'Dapur', 'Ruang Tamu', 'Outdoor', 'Kedai / Butik'] },
    { key: 'ekspresi', label: 'prostudio.style.field.expression', pilihan: [AUTO, 'Senyum Lembut', 'Ceria', 'Serius / Yakin'] },
    { key: 'cahaya', label: 'prostudio.style.field.light', pilihan: [AUTO, 'Cahaya Lembut', 'Rim Light', 'Warm Tone'] },
];

const SET_META = {
    all: { tajuk: 'prostudio.set.all.title', sub: 'prostudio.set.all.sub' },
    s1: { tajuk: 'prostudio.set.s1.title', sub: 'prostudio.set.s1.sub' },
    s2: { tajuk: 'prostudio.set.s2.title', sub: 'prostudio.set.s2.sub' },
    s3: { tajuk: 'prostudio.set.s3.title', sub: 'prostudio.set.s3.sub' },
};

const MODULES = [
    { id: 'ai8saat', nama: 'AI8SAAT', sedia: true },
    { id: 'ai16saat', nama: 'AI16SAAT', sedia: false },
    { id: 'homeniche', nama: 'Home Niche', sedia: false },
    { id: 'goyang', nama: 'GOYANG', sedia: false },
    { id: 'smartcustom', nama: 'Smart Custom', sedia: false },
    { id: 'flownity', nama: 'FLOWNITY', sedia: false },
];

const MAX_TEMPLATE = 5;   // nanti ikut lesen — mekanik sama maxProfiles avatar
const MAX_GROUP = 3;      // demo = kuota Premium; Fighter 6

const setKosong = () => ({ sudut: AUTO, lokasi: AUTO, ekspresi: AUTO, cahaya: AUTO, bebas: '' });
const rawakKosong = () => ({
    sudut: { on: false, picks: [] }, lokasi: { on: false, picks: [] },
    ekspresi: { on: false, picks: [] }, cahaya: { on: false, picks: [] },
});
const nilaiKosong = () => ({ all: setKosong(), s1: setKosong(), s2: setKosong(), s3: setKosong() });
const shuffleKosong = () => ({ all: rawakKosong(), s1: rawakKosong(), s2: rawakKosong(), s3: rawakKosong() });
const salin = (o) => JSON.parse(JSON.stringify(o));

function templateContoh() {
    const a = { nama: 'Supplement Style', mod: 'perset', nilai: nilaiKosong(), rawak: shuffleKosong() };
    a.nilai.s1 = { sudut: 'High Angle', lokasi: 'Bilik Moden', ekspresi: 'Serius / Yakin', cahaya: 'Cahaya Lembut', bebas: '' };
    a.nilai.s2 = { sudut: 'Eye Level', lokasi: 'Outdoor', ekspresi: 'Ceria', cahaya: 'Warm Tone', bebas: '' };
    a.nilai.s3 = { sudut: 'Low Angle', lokasi: 'Kedai / Butik', ekspresi: 'Senyum Lembut', cahaya: 'Rim Light', bebas: '' };
    a.rawak.s1.lokasi = { on: true, picks: ['Bilik Moden', 'Ruang Tamu'] };

    const b = { nama: 'Produk Dapur', mod: 'all', nilai: nilaiKosong(), rawak: shuffleKosong() };
    b.nilai.all = { sudut: 'Eye Level', lokasi: 'Dapur', ekspresi: 'Ceria', cahaya: 'Cahaya Lembut', bebas: '' };
    return [a, b];
}

/* Kunci `adapt`/`verbatim` = nilai data (disimpan pada group).
   Nilai di dalamnya = kunci kamus, diterjemah masa render. */
const GUNA = {
    adapt: {
        label: 'prostudio.hook.useAdapt',
        ringkas: 'prostudio.hook.useAdaptSummary',
        untuk: 'prostudio.hook.useAdaptFor',
    },
    verbatim: {
        label: 'prostudio.hook.useVerbatim',
        ringkas: 'prostudio.hook.useVerbatimSummary',
        untuk: 'prostudio.hook.useVerbatimFor',
    },
};

const mkHook = (teks) => ({ id: `h_${Math.random().toString(36).slice(2, 8)}`, teks });

const groupContoh = () => ([
    {
        id: 'g1', nama: 'Hook Mak-Mak Urgency', guna: 'adapt',
        nota: 'Nada mendesak tetapi mesra. Ayat pendek, seperti bercakap dengan kawan.',
        hooks: [
            mkHook('Hari ini sahaja murah, esok sudah naik!'),
            mkHook('Jangan beli kalau tidak mahu ketagih'),
            mkHook('Saya sudah cuba, memang berbaloi'),
            mkHook('Hari terakhir promo — selepas ini habis!'),
        ],
    },
    {
        id: 'g2', nama: 'Hook Simpanan Saya', guna: 'verbatim', nota: '',
        hooks: [
            mkHook('Tengok ini dahulu sebelum anda beli'),
            mkHook('Ini yang saya pakai setiap hari'),
            mkHook('Serius, saya tidak sangka jadi begini'),
        ],
    },
]);

/* Seksyen PRO Studio. `sedia` hanya menentukan lencana pada hub —
   tiga seksyen terakhir memang belum dibina, jadi ia mendarat pada
   skrin "Akan datang" dan bukan pada editor palsu. */
/* ⭐ `kumpulan` — keputusan user 2026-08-16.

   'semua'  = tetapan yang DISAPU ke atas apa sahaja yang dijalankan.
   'smart'  = milik satu module sahaja.

   My Agent tidak sekeluarga dengan lima yang lain: ia bukan lapisan di
   atas engine, ia yang MENJANA prompt (SMART menggunakan custom GPT
   pelanggan sebagai ganti master prompt SESAAT). Ia juga satu-satunya
   yang muncul di Workspace sebagai sasaran Run, dan satu-satunya yang
   berkuota.

   Dahulu ia baris kedua dalam senarai yang sama, jadi ia terbaca
   seolah-olah satu lagi tetapan. Memisahkannya dengan tajuk kumpulan
   membetulkan bacaan itu tanpa memindahkan apa-apa.

   ⚠️ Kalau Formula Sendiri dilancarkan kelak, semak semula: formula di
      rak /formula + agent di sini bermakna DUA pintu untuk satu soalan
      yang sama ("apa yang menjana prompt saya?"). Keputusan itu sengaja
      ditangguhkan, bukan terlepas pandang. */
const SEKSYEN = [
    {
        id: 'gaya', label: 'prostudio.section.gaya.label', ikon: Palette, sedia: true,
        kumpulan: 'semua', perihal: 'prostudio.section.gaya.desc',
    },
    {
        id: 'hook', label: 'prostudio.section.hook.label', ikon: Anchor, sedia: true,
        kumpulan: 'semua', perihal: 'prostudio.section.hook.desc',
    },
    {
        id: 'agent', label: 'prostudio.section.agent.label', ikon: Bot, sedia: true,
        kumpulan: 'smart', perihal: 'prostudio.section.agent.desc',
    },
    {
        id: 'cta', label: 'prostudio.section.cta.label', ikon: Hash, sedia: true,
        kumpulan: 'semua', perihal: 'prostudio.section.cta.desc',
    },
    /* Shortcut "/" (2026-09-25) — terbuka kepada SEMUA termasuk trial
       (trial had 1, dikuatkuasakan juga oleh firestore.rules). Baris
       BERASINGAN daripada "Prompt Saya" — itu kekal untuk idea asalnya
       (arahan tetap yang ditambah automatik), keputusan user. */
    {
        id: 'shortcut', label: 'prostudio.section.shortcut.label', ikon: Slash, sedia: true,
        kumpulan: 'semua', perihal: 'prostudio.section.shortcut.desc',
    },
    {
        id: 'prompt', label: 'prostudio.section.prompt.label', ikon: PenLine, sedia: false,
        kumpulan: 'semua', perihal: 'prostudio.section.prompt.desc',
    },
    {
        id: 'template', label: 'prostudio.section.template.label', ikon: Bookmark, sedia: false,
        kumpulan: 'semua', perihal: 'prostudio.section.template.desc',
    },
    /* ⚠️ PROTOTAIP — ADMIN SAHAJA. `prototaip: true` menapis baris ini
       daripada pelanggan (lihat `seksyenPapar`), jadi ia tidak muncul di
       hub DAN alamatnya dilencongkan ke hub. Ia duduk dalam kumpulannya
       sendiri supaya mata nampak ia bukan tetapan pelanggan. Buang
       bendera itu apabila bahagian sebenar siap. */
    {
        id: 'blok', label: 'prostudio.section.blok.label', ikon: Boxes, sedia: true,
        kumpulan: 'prototaip', perihal: 'prostudio.section.blok.desc', prototaip: true,
    },
];

/* Kepala halaman fokus (tingkat 2). Pautan kembali diletak DI ATAS
   tajuk, bukan di sebelahnya — corak yang sama dengan halaman
   Settings v3.9, supaya pengguna tidak perlu belajar dua cara
   keluar daripada satu skrin dalam app yang sama. */
const KepalaFokus = ({ tajuk, perihal, aksi, pautanKembali }) => (
    <>
        {/* Pautan sebenar, bukan butang — supaya klik kanan / Ctrl+klik
            pada "PRO Studio" berkelakuan seperti mana-mana pautan lain. */}
        <a
            {...pautanKembali}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 14,
                fontFamily: 'inherit', fontSize: 13.5, fontWeight: 500,
                color: 'var(--s-ink-3)', textDecoration: 'none',
            }}
        >
            <ArrowLeft size={15} strokeWidth={1.9} /> PRO Studio
        </a>
        <PageHeader title={tajuk} subtitle={perihal} actions={aksi} />
    </>
);

/* Kunci baca-sahaja untuk bukan-admin. `fieldset disabled` melumpuhkan
   SEMUA input/select/textarea/button di dalamnya sekali gus, jadi tiada
   satu kawalan pun terlepas bila seksyen baharu ditambah kemudian.

   ⚠️ Pautan "← PRO Studio" mesti tinggal DI LUAR pagar ini — ia butang,
      dan pelanggan yang masuk ke satu seksyen perlu boleh keluar.
   ⚠️ `minWidth: 0` wajib: fieldset lalai `min-width: min-content`, dan
      tanpa ini grid kolum set enggan mengecil pada skrin sempit.        */
const Pagar = ({ kunci, children }) => (kunci
    ? <fieldset disabled style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>{children}</fieldset>
    : children);

/* Rangka dua lajur: rel senarai di kiri, editor di kanan.
   Berpatah sendiri tanpa media query — rel `1 1 240px` dan editor
   `999 1 420px`, jadi editor sentiasa merampas ruang lebih dahulu
   dan kedua-duanya turun menjadi satu lajur bila skrin sempit. */
const DuaLajur = ({ rel, isi }) => (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 240px', maxWidth: 300, minWidth: 0 }}>{rel}</div>
        <div style={{ flex: '999 1 420px', minWidth: 0, display: 'grid', gap: 14 }}>{isi}</div>
    </div>
);

/* ============================================================
   KOMPONEN UTAMA
   ============================================================ */
/* `aksesSmart` / `sebabKunci` — dikira di App.jsx dengan penjaga lesen
   yang SAMA seperti Rak Formula. Hanya seksyen Agent Saya bergantung
   padanya.

   `aksesCta` — penjaga BERASINGAN, juga dikira di App.jsx. CTA beg kuning
   ialah ciri TikTok, bukan ciri SMART, jadi ia TIDAK boleh berkongsi
   `readOnly` (yang ialah kunci SMART) mahupun `aksesSmart`. Lalainya
   `false` = fail-closed: kalau App.jsx terlupa menghantarnya, CTA terkunci,
   bukan terbuka kepada semua orang. Mod pelan tidak terjejas — `ctaKunci`
   di bawah hanya menguatkuasakannya dalam mod sebenar. */
/* `isAdmin` — lalai `false` = FAIL-CLOSED, corak yang sama seperti
   `aksesCta`. Kalau App.jsx terlupa menghantarnya pada mana-mana route,
   seksyen prototaip tersembunyi, bukan terdedah kepada pelanggan. */
export default function ProStudioV39View({
    modSebenar = false, readOnly = false,
    aksesSmart = true, isAdmin = false, onNaikTaraf,
}) {
    /* ── Seksyen = ALAMAT, bukan keadaan dalaman ──────────────────────
       Setiap seksyen ada alamatnya sendiri (`/prostudio/gaya`, …) supaya
       ia boleh di-bookmark, dikongsi, dibuka dalam tab baharu, dan butang
       Back browser berfungsi. Sebelum ini kesemuanya satu alamat dengan
       useState — tekan Back membawa pengguna keluar terus dari PRO Studio,
       bukan kembali ke senarai.                                         */
    const navigate = useNavigate();
    const t = useT();
    /* `sub` = tingkat ketiga alamat. Setakat ini hanya seksyen Agent yang
       memakainya (/prostudio/agent/<id> atau /agent/baharu). */
    const { seksyen: paramSeksyen, sub } = useParams();
    const asas = modSebenar ? '/prostudio' : '/prostudio-v3.9';
    const alamat = (id) => (id ? `${asas}/${id}` : asas);

    /* Seksyen bertanda `prototaip` hanya wujud untuk admin. Ditapis di
       SINI, bukan di tempat render sahaja — supaya `/prostudio/blok`
       bagi bukan-admin dilencongkan ke hub oleh useEffect di bawah,
       bukan memapar skrin prototaip di bawah alamat pelanggan. */
    const seksyenPapar = SEKSYEN.filter((s) => !s.prototaip || isAdmin);
    const semasa = seksyenPapar.find((s) => s.id === paramSeksyen) || null;
    const seksyen = semasa?.id || null;

    /* Alamat yang tidak dikenali (salah taip, pautan lama) dibetulkan
       kepada hub — bukan dibiar memapar hub di bawah alamat yang salah. */
    useEffect(() => {
        if (paramSeksyen && !semasa) navigate(asas, { replace: true });
    }, [paramSeksyen, semasa, navigate, asas]);

    /* Pergi ke seksyen / balik ke hub. Dipakai oleh pautan di bawah —
       pautan itu KEKAL <a href> sebenar supaya klik kanan menawarkan
       "Open link in new tab"; ini hanya memintas klik kiri biasa. */
    const pergi = (id) => navigate(alamat(id));
    /* Hub boleh melompat terus ke satu agent (atau ke wizard kosong)
       tanpa singgah di halaman senarai. */
    const pergiAgent = (idAgent) => navigate(`${asas}/agent/${idAgent}`);
    const sifatPautan = (id) => ({
        href: alamat(id),
        onClick: (e) => { if (klikBiasa(e)) { e.preventDefault(); pergi(id); } },
    });

    /* ── Keadaan: Gaya ── */
    const [module, setModule] = useState('ai8saat');
    const [templates, setTemplates] = useState(templateContoh);
    const [pilihTpl, setPilihTpl] = useState('Supplement Style');
    const [defaultTpl, setDefaultTpl] = useState('Supplement Style');
    const [mod, setMod] = useState('perset');
    const [nilai, setNilai] = useState(() => salin(templateContoh()[0].nilai));
    const [rawak, setRawak] = useState(() => salin(templateContoh()[0].rawak));
    const [simpanKilat, setSimpanKilat] = useState(false);

    /* ── Keadaan: Hook Bank ── */
    const [groups, setGroups] = useState(groupContoh);
    /* Hub memapar agent SEBENAR dalam mod sebenar, dan agent contoh
       dalam mod pelan. Bacaan dimatikan bila pelanggan tiada akses
       SMART — tiada gunanya membaca senarai yang tidak akan dipapar. */
    const [agentPelan, setAgentPelan] = useState(agentContoh);
    const { agents: agentSebenar } = useSenaraiAgent(modSebenar && aksesSmart);
    const agents = modSebenar ? agentSebenar : agentPelan;

    /* ── CTA beg kuning ──────────────────────────────────────────
       Mod sebenar = Firestore (snapshot hidup memapar hasilnya sendiri,
       jadi tiada salinan tempatan kedua). Mod pelan = keadaan tempatan
       supaya lakaran boleh dinilai tanpa menulis ke akaun sesiapa. */
    const [ctaPelan, setCtaPelan] = useState([]);
    const { senarai: ctaSebenar, sudahMuat: ctaMuatSebenar } = useSenaraiCta(modSebenar);
    const ctaSenarai = modSebenar ? ctaSebenar : ctaPelan;
    const ctaSudahMuat = modSebenar ? ctaMuatSebenar : true;
    /* Kunci CTA — SATU takrifan, dipakai oleh lencana hub, sepanduk
       pratonton dan pagar halaman. Kalau ia dikira semula di tiga tempat,
       satu daripadanya akan terlupa dikemas kini dan pelanggan mendapat
       lencana "3 CTA" di atas skrin yang mati. Mod pelan sentiasa terbuka:
       lakaran memang wujud untuk dinilai, bukan untuk dijaga lesen. */
    /* 🔒 [2026-09-26] CTA kini ikut CLAIM PERCUMA (keputusan user: claim ikut
       ciri, termasuk trial, hingga 30 Nov 2026) — menggantikan `aksesCta`
       (berbayar bukan-trial). Prop `aksesCta` dari App.jsx kini DIABAIKAN
       (tidak dibaca). KEMBAR: kunci dropdown CTA di Control Panel
       (PautanProdukV39) + `ctaTeksLarian` App.jsx. */
    const ciriP = useCiriPercuma(modSebenar);
    const statusCta = modSebenar ? ciriP.status(CIRI.CTA) : 'aktif';
    const statusSc = modSebenar ? ciriP.status(CIRI.SHORTCUT) : 'aktif';
    const ctaKunci = modSebenar && !bolehGunaCiri(statusCta);

    const ctaSimpan = async ({ id, teks }) => {
        if (!modSebenar) {
            const bersih = bersihkanCta(teks);
            if (!bersih) return;
            setCtaPelan((prev) => (id
                ? prev.map((x) => (x.id === id ? { ...x, teks: bersih } : x))
                : [...prev, { id: `pelan_${Date.now()}`, teks: bersih }]));
            return;
        }
        await simpanCta({ id, teks });
    };

    const ctaPadam = async (id) => {
        if (!modSebenar) { setCtaPelan((prev) => prev.filter((x) => x.id !== id)); return; }
        await padamCta(id);
    };

    /* ── Popup claim ciri percuma ────────────────────────────────
       Auto: bila halaman ciri itu dibuka, belum claim, dan belum ditolak
       "Nanti dulu" hari ini. Manual: butang "Aktifkan percuma". Dikira
       semasa render (bukan useEffect) — tiada setState dalam effect. */
    const [popupCiri, setPopupCiri] = useState(null);
    const [popupDitutup, setPopupDitutup] = useState({});
    const ciriHalaman = paramSeksyen === 'shortcut' ? CIRI.SHORTCUT : paramSeksyen === 'cta' ? CIRI.CTA : null;
    const popupAuto = modSebenar && ciriP.sudahMuat && ciriHalaman
        && ciriP.status(ciriHalaman) === 'belum' && !popupDitutup[ciriHalaman] && !nantiHariIni(ciriHalaman)
        ? ciriHalaman : null;
    const popupPapar = popupCiri || popupAuto;
    const tutupPopup = () => {
        if (popupPapar) setPopupDitutup((p) => ({ ...p, [popupPapar]: true }));
        setPopupCiri(null);
    };

    /* ── Shortcut "/" ────────────────────────────────────────────
       Corak sama CTA: mod sebenar = Firestore (snapshot hidup memapar
       hasilnya), mod pelan = keadaan tempatan, tiada tulisan. */
    const [scPelan, setScPelan] = useState([]);
    const scSebenar = useSenaraiShortcut(modSebenar);
    const scSenarai = modSebenar ? scSebenar.senarai : scPelan;
    const scSimpan = async ({ id, nama, teks }) => {
        if (!modSebenar) {
            const kod = semakShortcut({ id, nama, teks }, scPelan, false);
            if (kod) throw new Error(kod);
            const item = { nama: bersihkanNama(nama), teks: bersihkanTeks(teks) };
            setScPelan((prev) => (id
                ? prev.map((x) => (x.id === id ? { ...x, ...item } : x))
                : [...prev, { id: `s${String(prev.length + 1).padStart(3, '0')}`, ...item }]));
            return;
        }
        await simpanShortcut({ id, nama, teks }, scSebenar.senarai, bolehGunaCiri(statusSc));
    };
    const scPadam = async (id) => {
        if (!modSebenar) { setScPelan((prev) => prev.filter((x) => x.id !== id)); return; }
        await padamShortcut(id);
    };
    const [pilihGroup, setPilihGroup] = useState('g1');
    const [defaultGroup, setDefaultGroup] = useState('g2');
    const [pukalBuka, setPukalBuka] = useState(false);
    const [pukalTeks, setPukalTeks] = useState('');
    const [hookKilat, setHookKilat] = useState(false);

    const penuhTpl = templates.length >= MAX_TEMPLATE;
    const penuhGroup = groups.length >= MAX_GROUP;
    const bakul = mod === 'all' ? ['all'] : ['s1', 's2', 's3'];
    const group = groups.find((g) => g.id === pilihGroup) || null;

    /* ── Tindakan: susunan agent ──────────────────────────────────
       Dipanggil dengan senarai id dalam susunan baharu.

       Mod sebenar menulis medan susunan ke Firestore; pendengar snapshot
       dalam useSenaraiAgent yang memapar hasilnya. Firestore memberi
       tulisan setempat serta-merta kepada pendengarnya sendiri, jadi baris
       bergerak tanpa menunggu jaringan — tiada keadaan tempatan kedua
       diperlukan di sini, dan kalau ada ia akan bertelagah dengan snapshot.

       Mod pelan hanya menyusun data contoh dalam ingatan. Sengaja: halaman
       pelan tidak boleh menyentuh data pelanggan, tetapi susunan yang mati
       di sana akan membuat lakaran itu terbaca sebagai rosak. */
    const susunAgent = async (idsBaru) => {
        if (!modSebenar) {
            setAgentPelan((prev) => idsBaru.map((id) => prev.find((a) => a.id === id)).filter(Boolean));
            return;
        }
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        try {
            await simpanSusunanAgent(uid, idsBaru);
        } catch (e) {
            console.error('[ProStudio] simpan susunan agent gagal:', e);
            alert(t('prostudio.wizard.orderFail') + (e.message || e));
        }
    };

    /* ── Tindakan: padam agent ───────────────────────────────────
       Dalam rupa v3.9, senarai agent hidup DI SINI sahaja — skrin senarai
       di dalam halaman Agent dibuang 2026-08-21 atas arahan user — jadi
       tong sampah pada baris ini ialah satu-satunya laluan padam yang
       tinggal. Tanpa ia, pelanggan langsung tidak boleh membuang agent.

       Pengesahan WAJIB: padam tidak boleh dibatalkan, dan butangnya duduk
       betul-betul di sebelah baris yang pelanggan buka setiap hari.

       Mod sebenar memadam doc; pendengar snapshot yang mengeluarkan baris
       daripada skrin — jadi TIADA keadaan tempatan kedua di sini, sama
       seperti laluan susun di atas. Mod pelan hanya membuang daripada data
       contoh dalam ingatan. */
    const padamAgent = async (id) => {
        const a = agents.find((x) => x.id === id);
        if (!window.confirm(t('prostudio.agentsec.confirmDelete', { nama: a?.data?.nama || '' }))) return;
        if (!modSebenar) {
            setAgentPelan((prev) => prev.filter((x) => x.id !== id));
            return;
        }
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        try {
            await padamAgentDoc(uid, id);
        } catch (e) {
            console.error('[ProStudio] padam agent gagal:', e);
            alert(t('prostudio.agentsec.errDelete') + (e.message || e));
        }
    };

    /* ── Tindakan: toggle "Papar di Workspace" (arahan user 2026-10-18) ──
       OFF = agent disorok daripada pemilih agent Workspace; agent tidak
       dipadam. Sama seperti susun/padam: tiada keadaan tempatan kedua —
       snapshot yang mengemas kini baris. Gagal = alert (bukan senyap),
       kerana pelanggan akan menyangka agent itu sudah disorok. */
    const togolWorkspace = async (id, papar) => {
        if (!modSebenar) {
            setAgentPelan((prev) => prev.map((x) => (x.id === id ? { ...x, diWorkspace: papar } : x)));
            return;
        }
        try {
            await tetapAgentDiWorkspace(auth.currentUser?.uid, id, papar);
        } catch (e) {
            console.error('[ProStudio] simpan Papar di Workspace gagal:', e);
            alert(t('prostudio.wizard.showInWsFail') + (e.message || e));
        }
    };

    /* ── Tindakan: Gaya ── */
    const muatTemplate = (t) => {
        setPilihTpl(t.nama);
        setMod(t.mod);
        setNilai(salin(t.nilai));
        setRawak(salin(t.rawak));
    };

    const templateBaharu = () => {
        if (penuhTpl) return;
        let n = 1;
        while (templates.some((t) => t.nama === `Template Baharu ${n}`)) n++;
        const t = { nama: `Template Baharu ${n}`, mod: 'all', nilai: nilaiKosong(), rawak: shuffleKosong() };
        setTemplates((p) => [...p, t]);
        muatTemplate(t);
    };

    const simpanTemplate = () => {
        if (!pilihTpl) return;
        setTemplates((p) => p.map((t) => (t.nama === pilihTpl
            ? { ...t, mod, nilai: salin(nilai), rawak: salin(rawak) }
            : t)));
        setSimpanKilat(true);
        setTimeout(() => setSimpanKilat(false), 1500);
    };

    const padamTemplate = (nama) => {
        setTemplates((p) => p.filter((t) => t.nama !== nama));
        if (defaultTpl === nama) setDefaultTpl('');
        if (pilihTpl === nama) {
            setPilihTpl('');
            setNilai(nilaiKosong());
            setRawak(shuffleKosong());
            setMod('all');
        }
    };

    /* Nama template ditukar terus dalam editor. Senarai di rel dan
       tanda "default" mesti ikut serentak, kalau tidak baris rel
       menjadi yatim sebaik nama bertukar. */
    const namakanTemplate = (nama) => {
        setTemplates((p) => p.map((t) => (t.nama === pilihTpl ? { ...t, nama } : t)));
        if (defaultTpl === pilihTpl) setDefaultTpl(nama);
        setPilihTpl(nama);
    };

    const tetapNilai = (bakulId, key, v) =>
        setNilai((p) => ({ ...p, [bakulId]: { ...p[bakulId], [key]: v } }));

    const suisRawak = (bakulId, key) =>
        setRawak((p) => {
            const kini = p[bakulId][key];
            return { ...p, [bakulId]: { ...p[bakulId], [key]: { ...kini, on: !kini.on } } };
        });

    const tandaRawak = (bakulId, key, opsyen) =>
        setRawak((p) => {
            const kini = p[bakulId][key];
            const picks = kini.picks.includes(opsyen)
                ? kini.picks.filter((x) => x !== opsyen)
                : [...kini.picks, opsyen];
            return { ...p, [bakulId]: { ...p[bakulId], [key]: { ...kini, picks } } };
        });

    const salinKeSetLain = (dari) => {
        setNilai((p) => ({ ...p, s1: salin(p[dari]), s2: salin(p[dari]), s3: salin(p[dari]) }));
        setRawak((p) => ({ ...p, s1: salin(p[dari]), s2: salin(p[dari]), s3: salin(p[dari]) }));
    };

    /* ── Tindakan: Hook Bank ── */
    const tampalGroup = (patch) =>
        setGroups((p) => p.map((g) => (g.id === pilihGroup ? { ...g, ...patch } : g)));

    const groupBaharu = () => {
        if (penuhGroup) return;
        let n = 1;
        while (groups.some((g) => g.nama === `Group Baharu ${n}`)) n++;
        const g = { id: `g_${Math.random().toString(36).slice(2, 8)}`, nama: `Group Baharu ${n}`, guna: 'adapt', nota: '', hooks: [] };
        setGroups((p) => [...p, g]);
        setPilihGroup(g.id);
    };

    const padamGroup = (id) => {
        setGroups((p) => p.filter((g) => g.id !== id));
        if (defaultGroup === id) setDefaultGroup('');
        if (pilihGroup === id) setPilihGroup('');
    };

    const tambahHook = () => tampalGroup({ hooks: [...(group?.hooks || []), mkHook('')] });

    const tukarHook = (id, teks) =>
        tampalGroup({ hooks: group.hooks.map((h) => (h.id === id ? { ...h, teks } : h)) });

    const buangHook = (id) => tampalGroup({ hooks: group.hooks.filter((h) => h.id !== id) });

    const tambahPukal = () => {
        const baris = pukalTeks.split('\n').map((x) => x.trim()).filter(Boolean);
        if (!baris.length) return;
        tampalGroup({ hooks: [...group.hooks, ...baris.map(mkHook)] });
        setPukalTeks('');
        setPukalBuka(false);
    };

    const simpanHook = () => {
        setHookKilat(true);
        setTimeout(() => setHookKilat(false), 1500);
    };

    /* ============================================================
       TINGKAT 1 — HUB
       ============================================================ */
    /* Lencana status pada setiap baris hub.
         Mod pelan   — kiraan data contoh; ia menggambarkan keadaan siap
                       yang dituju, itulah gunanya halaman pelan.
         Mod sebenar — hanya Agent Saya dan CTA beg kuning yang benar-benar
                       menyimpan. Gaya dan Hook Bank kekal "Akan datang"
                       supaya pelanggan tidak menyangka tetapan mereka sudah
                       terpakai. */
    const lencana = (s) => {
        if (!s.sedia) return { teks: t('prostudio.soon'), tone: 'neutral' };
        /* Prototaip: tiada lencana. Kumpulannya sendiri sudah bertanda
           Admin, dan "Akan datang" di sini akan menipu — ia memang boleh
           dibuka sekarang. */
        if (s.prototaip) return null;
        /* CTA dikira SEBELUM cabang mod pelan dan sebelum peraturan "selain
           Agent Saya = Akan datang" di bawah. Peraturan itu ditulis ketika
           hanya Agent Saya yang hidup dan ia tidak dikemas kini semasa CTA
           dilancarkan (4 Sept), jadi baris CTA bertanda "Akan datang" walau
           kepada admin yang memang boleh menggunakannya.
           Tiada akses = TIADA lencana, sama seperti Agent Saya: menulis
           kiraan pada baris yang membawa ke skrin mati adalah bercanggah
           sendiri. */
        if (s.id === 'cta') {
            if (statusCta === 'belum') return { teks: t('ciri.badgeShort'), tone: 'ok' };
            return ctaKunci
                ? null
                : { teks: t('prostudio.hub.badge.cta', { n: ctaSenarai.length }), tone: 'neutral' };
        }
        if (s.id === 'shortcut') {
            if (statusSc === 'belum') return { teks: t('ciri.badgeShort'), tone: 'ok' };
            return { teks: t('prostudio.hub.badge.shortcut', { n: scSenarai.length }), tone: 'neutral' };
        }
        if (!modSebenar) {
            return {
                teks: {
                    gaya: t('prostudio.hub.badge.templates', { n: templates.length }),
                    agent: t('prostudio.hub.badge.agents', { n: agents.length }),
                    hook: t('prostudio.hub.badge.groups', { n: groups.length }),
                }[s.id],
                tone: 'neutral',
            };
        }
        if (s.id !== 'agent') return { teks: t('prostudio.soon'), tone: 'neutral' };
        /* Tiada akses = TIADA lencana. Menulis "Ready" pada baris yang
           membawa ke skrin berkunci adalah bercanggah sendiri. */
        return aksesSmart ? { teks: t('prostudio.ready'), tone: 'ok' } : null;
    };

    /* Baris seksyen. Satu takrifan, dipakai oleh KEDUA-DUA kumpulan —
       kalau ia disalin, baris dalam panel SMART akan terpesong daripada
       baris di atasnya sebaik salah satu diubah.

       Baris = PAUTAN sebenar, bukan div boleh klik. Peraturan yang sama
       seperti rel menu (16 Ogos): apa sahaja yang membawa pengguna ke
       halaman lain mesti ada `href`, kalau tidak klik kanan tidak
       menawarkan "Open link in new tab" dan Ctrl+klik mati.
       `sifatPautan` yang memintas klik kiri biasa. */
    const barisSeksyen = (kump) => seksyenPapar.filter((s) => s.kumpulan === kump).map((s) => {
        const Ikon = s.ikon;
        const tanda = lencana(s);
        return (
            <Row key={s.id} {...sifatPautan(s.id)} style={{ cursor: 'pointer' }}>
                <span style={{
                    width: 34, height: 34, flex: 'none', borderRadius: 9,
                    display: 'grid', placeItems: 'center',
                    background: 'var(--s-fill)', color: 'var(--s-ink-2)',
                }}>
                    <Ikon size={16} strokeWidth={1.9} />
                </span>

                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="s-cardname">{t(s.label)}</div>
                    <div className="s-dim" style={{ marginTop: 2 }}>{t(s.perihal)}</div>
                </div>

                {tanda && <Badge tone={tanda.tone}>{tanda.teks}</Badge>}
                <ChevronRight size={16} strokeWidth={1.9} style={{ color: 'var(--s-ink-3)', flex: 'none' }} />
            </Row>
        );
    });

    const hub = (
        <>
            {/* ⛔ Lencana "Premium Pro · Fighter Pro" DIBUANG 2026-08-17
                (arahan user). Kedua-dua pakej itu tidak wujud dalam sistem
                lesen — yang ada hanya Premium, Fighter dan Custom. Lencana
                yang menamakan pakej yang tidak boleh dibeli menimbulkan
                soalan yang support terpaksa jawab.
                Pulangkan ia HANYA selepas pakej itu betul-betul wujud. */}
            <PageHeader
                title="PRO Studio"
                subtitle={t('prostudio.hub.subtitle')}
            />

            {/* Petak angka — menjawab "berapa banyak yang saya sudah set"
                tanpa perlu masuk ke dalam setiap seksyen dahulu.

                ⛔ MOD PELAN SAHAJA. Dalam mod sebenar angka ini datang
                   daripada data contoh dalam fail ini, jadi memaparkannya
                   kepada pelanggan bermakna memberitahu dia bahawa dia ada
                   dua template bernama "Supplement Style" — yang tidak
                   benar. Ia dipulangkan bila simpanan sebenar siap. */}
            {!modSebenar && (
                <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
                    gap: 12, marginBottom: 20,
                }}>
                    <StatTile label={t('prostudio.hub.stat.templates')} value={`${templates.length}/${MAX_TEMPLATE}`}
                        hint={t('prostudio.hub.stat.templatesHint')} icon={<Palette size={15} strokeWidth={1.9} />} />
                    <StatTile label={t('prostudio.hub.stat.groups')} value={`${groups.length}/${MAX_GROUP}`}
                        hint={t('prostudio.hub.stat.groupsHint')} icon={<Anchor size={15} strokeWidth={1.9} />} />
                    {/* Bilangan sahaja — tiada had bilangan agent lagi
                        (arahan user 2026-08-16). Angka datang daripada
                        senarai yang SAMA seperti panel SMART di bawah. */}
                    <StatTile label={t('prostudio.hub.stat.agents')} value={String(agents.length)}
                        hint={t('prostudio.hub.stat.agentsHint')} icon={<Bot size={15} strokeWidth={1.9} />} />
                </div>
            )}

            {/* Sepanduk pratonton — dibawa daripada halaman lama. Pelanggan
                dibenarkan meninjau supaya dia tahu apa yang akan datang,
                tetapi tiada satu kawalan pun boleh disentuh.
                ⛔ Ia HILANG sebaik pelanggan mendapat akses CTA: ayatnya
                berkata "tetapan belum boleh diubah", dan itu menjadi tidak
                benar sebaik satu bahagian boleh diubah. Tiada penjelasan
                yang hilang — baris lain masih bertanda "Akan datang" dan
                Agent Saya membawa skrin jualannya sendiri. */}
            {modSebenar && readOnly && ctaKunci && (
                <div style={{ marginBottom: 20 }}>
                    <Nota>
                        <strong style={{ color: 'var(--s-ink)' }}>{t('prostudio.hub.previewTitle')}</strong>{' '}
                        {t('prostudio.hub.previewBody')}
                    </Nota>
                </div>
            )}

            {/* ── Kumpulan 1: badan halaman ──────────────────────────
                Label kecil huruf besar, senarai terus di atas latar
                halaman. Ini keadaan "biasa" — kumpulan kedua di bawah
                yang dibezakan, bukan yang ini. */}
            <div style={{ marginBottom: 22 }}>
                <div style={{ marginBottom: 9 }}>
                    <div style={{
                        fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em',
                        textTransform: 'uppercase', color: 'var(--s-ink-3)',
                    }}>{t('prostudio.hub.group.all')}</div>
                    <div className="s-dim" style={{ marginTop: 3 }}>{t('prostudio.hub.group.allDesc')}</div>
                </div>
                <List>{barisSeksyen('semua')}</List>
            </div>

            {/* ── Kumpulan 2: dikurung dalam panelnya sendiri ─────────
                Keputusan user 2026-08-16 (pilihan B daripada tiga lakaran).
                Dua label kecil yang serupa membuatkan kedua-dua kumpulan
                terbaca sebagai satu senarai panjang; mengurung yang kedua
                memberi mata sempadan yang sebenar.

                Latar `--s-ground` DIPILIH dengan sengaja: ia latar halaman,
                jadi panel ini terbaca sebagai lekukan (recess) dan kad
                senarai di dalamnya kelihatan terapung di atasnya. Kedua-dua
                tema berfungsi — terang #FAFAFA memegang kad putih, gelap
                #111111 memegang kad #181818.
                ⛔ Jangan tukar kepada --s-card: ia warna yang SAMA dengan
                   senarai di dalamnya, jadi sempadan itu hilang terus. */}
            <div style={{
                marginBottom: 22, padding: 14, borderRadius: 'var(--s-r-lg)',
                background: 'var(--s-ground)', border: '1px solid var(--s-line)',
            }}>
                {/* ⭐ Senarai agent dipapar TERUS di sini (arahan user
                    2026-08-16), bukan di sebalik satu baris "My Agent"
                    yang perlu diklik dahulu. Baris itu tidak membawa
                    maklumat sendiri — ia hanya menulis bilangan, kemudian
                    menghantar pelanggan ke halaman yang menyenaraikan
                    benda yang sama. Satu tingkat yang tidak berbayar. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                    <Boxes size={15} strokeWidth={1.9} style={{ color: 'var(--s-ink-2)', flex: 'none' }} />
                    <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--s-ink)' }}>
                        {t('prostudio.hub.group.smart')}
                    </span>
                    {/* ⛔ TIADA lencana amaran di sini bila akses tiada.
                        "Not available" terbaca sebagai sistem rosak (aduan user
                        2026-08-17). Panel kekal nampak biasa; pelanggan hanya
                        bertemu pagar SELEPAS dia klik masuk, di mana ada ayat
                        penuh dan butang naik taraf. */}
                    {aksesSmart && <Badge tone="neutral">{t('prostudio.hub.badge.agents', { n: agents.length })}</Badge>}
                    <span style={{ flex: 1 }} />
                    {(
                        <Btn
                            variant="ghost"
                            size="sm"
                            icon={<Plus size={14} strokeWidth={2} />}
                            onClick={() => pergiAgent('baharu')}
                        >
                            {t('prostudio.wizard.add')}
                        </Btn>
                    )}
                </div>
                <div className="s-dim" style={{ marginBottom: 11 }}>{t('prostudio.hub.group.smartDesc')}</div>

                {/* ⛔ Senarai agent inline ialah LAKARAN — data contoh dalam
                    fail ini. Dalam mod SEBENAR ia menjadi nama rekaan yang
                    dipapar kepada pelanggan sebagai agentnya sendiri, jadi
                    mod sebenar memapar baris seksyen biasa yang membawa ke
                    halaman Agent, dan halaman itulah yang memuatkan agent
                    betul daripada Firestore.
                    Buang pagar ini HANYA selepas hub membaca agent sebenar. */}
                {!aksesSmart || agents.length === 0
                    ? <List>{barisSeksyen('smart')}</List>
                    : (
                        /* `padam` diberi DI SINI (hub tidak memberinya dahulu).
                           Sebabnya: halaman Agent v3.9 tidak lagi memapar
                           senarai, jadi tong sampah pada baris ini satu-satunya
                           laluan padam yang tinggal. */
                        <SenaraiAgentV39
                            agents={agents}
                            onBuka={(a) => pergiAgent(a.id)}
                            onSusun={susunAgent}
                            padam={padamAgent}
                            onTogolWorkspace={togolWorkspace}
                        />
                    )}
            </div>

            {/* ── Kumpulan 3: PROTOTAIP (admin sahaja) ────────────────
                Panel terkurung yang SAMA seperti kumpulan SMART, supaya
                mata terus nampak ia bukan sebahagian tetapan pelanggan.
                Pagar `isAdmin` di sini berpasangan dengan `seksyenPapar`
                di atas — satu menyorok baris, satu lagi menutup alamat. */}
            {isAdmin && (
                <div style={{
                    marginBottom: 22, padding: 14, borderRadius: 'var(--s-r-lg)',
                    background: 'var(--s-ground)', border: '1px solid var(--s-line)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                        <FlaskConical size={15} strokeWidth={1.9} style={{ color: 'var(--s-ink-2)', flex: 'none' }} />
                        <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--s-ink)' }}>
                            {t('prostudio.hub.group.proto')}
                        </span>
                        <Badge tone="neutral">Admin</Badge>
                    </div>
                    <div className="s-dim" style={{ marginBottom: 11 }}>
                        {t('prostudio.hub.group.protoDesc')}
                    </div>
                    <List>{barisSeksyen('prototaip')}</List>
                </div>
            )}

            <div style={{ marginTop: 16 }}>
                <Nota jenis="warn">
                    {t('prostudio.hub.coreRule')}
                </Nota>
            </div>
        </>
    );

    /* ============================================================
       TINGKAT 2 — GAYA
       ============================================================ */
    const medanGaya = (bakulId, m) => {
        const sh = rawak[bakulId][m.key];
        return (
            <div key={m.key}>
                <Lbl
                    aksi={
                        <Cip
                            kecil
                            on={sh.on}
                            onClick={() => suisRawak(bakulId, m.key)}
                            ikon={<Shuffle size={12} strokeWidth={2} />}
                            title={sh.on
                                ? t('prostudio.style.randomOff')
                                : t('prostudio.style.randomOn')}
                        >
                            {t('prostudio.style.random')}
                        </Cip>
                    }
                >
                    {t(m.label)}
                </Lbl>

                {!sh.on ? (
                    <Sel
                        auto={nilai[bakulId][m.key] === AUTO}
                        value={nilai[bakulId][m.key]}
                        onChange={(e) => tetapNilai(bakulId, m.key, e.target.value)}
                    >
                        {/* `value` kekal nilai data; hanya labelnya diterjemah. */}
                        {m.pilihan.map((o) => (
                            <option key={o} value={o}>{o === AUTO ? t('prostudio.style.auto') : o}</option>
                        ))}
                    </Sel>
                ) : (
                    <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: 6, padding: 9,
                        borderRadius: 8, background: 'var(--s-fill)',
                        border: '1px dashed var(--s-line-strong)',
                    }}>
                        {m.pilihan.filter((o) => o !== AUTO).map((o) => (
                            <Cip key={o} kecil on={sh.picks.includes(o)} onClick={() => tandaRawak(bakulId, m.key, o)}>
                                {o}
                            </Cip>
                        ))}
                        <div style={{ width: '100%', fontSize: 11.5, color: 'var(--s-ink-3)', marginTop: 2 }}>
                            {sh.picks.length === 0
                                ? t('prostudio.style.randomEmpty')
                                : t('prostudio.style.randomCount', { n: sh.picks.length })}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const lajurSet = (bakulId) => (
        <Kad
            key={bakulId}
            tajuk={t(SET_META[bakulId].tajuk)}
            perihal={t(SET_META[bakulId].sub)}
            kanan={bakulId !== 'all' && (
                <IconBtn
                    label={t('prostudio.style.copyToOther')}
                    icon={<Copy size={14} strokeWidth={1.9} />}
                    onClick={() => salinKeSetLain(bakulId)}
                />
            )}
        >
            <div style={{ display: 'grid', gap: 12 }}>
                {MEDAN.map((m) => medanGaya(bakulId, m))}
                <div>
                    <Lbl>{t('prostudio.style.extraInstruction')}</Lbl>
                    <Ta
                        value={nilai[bakulId].bebas}
                        onChange={(e) => tetapNilai(bakulId, 'bebas', e.target.value)}
                        placeholder={t('prostudio.style.extraPlaceholder')}
                        style={{ minHeight: 54 }}
                    />
                </div>
            </div>
        </Kad>
    );

    /* ⚠️ Teks baris senarai template dikira DI SINI, bukan di dalam
       gelung di bawah: parameter `templates.map((t) => …)` menaungi
       (shadow) fungsi terjemahan `t`, jadi memanggil t('…') di dalam
       gelung itu bermakna memanggil objek template. */
    const txtTplDefault = t('prostudio.style.isDefaultTpl');
    const txtTplMakeDefault = t('prostudio.style.makeDefaultTpl');
    const txtTplPerSet = t('prostudio.style.perSet');
    const txtTplAllSame = t('prostudio.style.allSetsSame');
    const txtTplDelete = t('prostudio.style.deleteTemplate');

    const halamanGaya = (
        <>
            <KepalaFokus
                tajuk={t('prostudio.section.gaya.label')}
                perihal={t('prostudio.style.pageDesc')}
                pautanKembali={sifatPautan(null)}
                aksi={<Badge tone="neutral">{module.toUpperCase()}</Badge>}
            />

            <Pagar kunci={readOnly}>
            {/* Pemilih module. Pil, bukan segmented — enam module tidak
                muat pada satu bar segmented, dan pil memberi ruang untuk
                ikon kunci pada module yang belum dibuka. */}
            <div style={{ marginBottom: 16 }}>
                <Lbl>{t('prostudio.style.module')}</Lbl>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                    {MODULES.map((m) => (
                        <Cip
                            key={m.id}
                            on={module === m.id}
                            kunci={!m.sedia}
                            onClick={() => m.sedia && setModule(m.id)}
                            ikon={!m.sedia ? <Lock size={12} strokeWidth={2} /> : null}
                            title={m.sedia
                                ? t('prostudio.style.moduleTitle', { nama: m.nama })
                                : t('prostudio.style.moduleSoon', { nama: m.nama })}
                        >
                            {m.nama}
                        </Cip>
                    ))}
                </div>
            </div>

            <DuaLajur
                rel={
                    <Kad
                        tajuk={t('prostudio.style.templatesTitle')}
                        perihal={t('prostudio.style.templatesDesc')}
                        kanan={<Badge tone={penuhTpl ? 'warn' : 'neutral'}>{templates.length}/{MAX_TEMPLATE}</Badge>}
                        kaki={
                            <Btn variant="ghost" size="sm" disabled={penuhTpl} onClick={templateBaharu}
                                icon={<Plus size={14} strokeWidth={2} />} style={{ width: '100%' }}>
                                {penuhTpl ? t('prostudio.slotFull') : t('prostudio.style.newTemplate')}
                            </Btn>
                        }
                    >
                        <div style={{ display: 'grid', gap: 6 }}>
                            {templates.length === 0 && (
                                <span className="s-dim">{t('prostudio.style.noTemplate')}</span>
                            )}
                            {templates.map((t) => {
                                const aktif = pilihTpl === t.nama;
                                const isDefault = defaultTpl === t.nama;
                                return (
                                    <div
                                        key={t.nama}
                                        onClick={() => muatTemplate(t)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            padding: '8px 10px', borderRadius: 9, cursor: 'pointer',
                                            background: aktif ? 'var(--s-fill)' : 'transparent',
                                            border: `1px solid ${aktif ? 'var(--s-line-strong)' : 'transparent'}`,
                                        }}
                                    >
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setDefaultTpl(isDefault ? '' : t.nama); }}
                                            title={isDefault ? txtTplDefault : txtTplMakeDefault}
                                            style={{
                                                display: 'flex', padding: 0, background: 'none', border: 0,
                                                cursor: 'pointer', color: isDefault ? 'var(--s-ink)' : 'var(--s-ink-3)',
                                            }}
                                        >
                                            <Star size={15} strokeWidth={1.9} fill={isDefault ? 'currentColor' : 'none'} />
                                        </button>

                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontSize: 13.5, fontWeight: 500, color: 'var(--s-ink)',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>{t.nama}</div>
                                            <div style={{ fontSize: 11.5, color: 'var(--s-ink-3)' }}>
                                                {t.mod === 'perset' ? txtTplPerSet : txtTplAllSame}
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); padamTemplate(t.nama); }}
                                            title={txtTplDelete}
                                            style={{
                                                display: 'flex', padding: 2, background: 'none', border: 0,
                                                cursor: 'pointer', color: 'var(--s-ink-3)',
                                            }}
                                        >
                                            <Trash2 size={14} strokeWidth={1.9} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </Kad>
                }
                isi={
                    pilihTpl ? (
                        <>
                            {/* Bar editor: nama + mod + simpan. Nama boleh ditaip
                                terus di sini — halaman lama tiada langsung cara
                                menamakan semula template selepas ia dicipta. */}
                            <div className="s-panelbox" style={{ padding: 14 }}>
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                    <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                                        <Lbl>{t('prostudio.style.templateName')}</Lbl>
                                        <Inp value={pilihTpl} onChange={(e) => namakanTemplate(e.target.value)} />
                                    </div>
                                    <div style={{ flex: '0 0 auto' }}>
                                        <Lbl>{t('prostudio.style.applyBy')}</Lbl>
                                        <Segmented
                                            value={mod}
                                            onChange={setMod}
                                            options={[
                                                { value: 'all', label: t('prostudio.style.allSets') },
                                                { value: 'perset', label: t('prostudio.style.perSet') },
                                            ]}
                                        />
                                    </div>
                                    <Btn onClick={simpanTemplate}
                                        icon={simpanKilat ? <Check size={14} strokeWidth={2} /> : null}>
                                        {simpanKilat ? t('prostudio.saved') : t('common.save')}
                                    </Btn>
                                </div>
                            </div>

                            {/* Kolum set. Sebab utama layout ditukar: di sini
                                editor dapat lebar penuh panel, jadi tiga set
                                muat sebelah-menyebelah tanpa berhimpit. */}
                            <div style={{
                                display: 'grid', gap: 14,
                                gridTemplateColumns: mod === 'all'
                                    ? 'minmax(0, 460px)'
                                    : 'repeat(auto-fit, minmax(250px, 1fr))',
                            }}>
                                {bakul.map(lajurSet)}
                            </div>
                        </>
                    ) : (
                        <EmptyState title={t('prostudio.style.emptyTitle')}>
                            {t('prostudio.style.emptyBody')}
                        </EmptyState>
                    )
                }
            />
            </Pagar>
        </>
    );

    /* ============================================================
       TINGKAT 2 — HOOK BANK
       ============================================================ */
    const halamanHook = (
        <>
            <KepalaFokus
                tajuk={t('prostudio.section.hook.label')}
                perihal={t('prostudio.hook.pageDesc')}
                pautanKembali={sifatPautan(null)}
                aksi={<Badge tone="neutral">{t('prostudio.hook.sharedBadge')}</Badge>}
            />

            <Pagar kunci={readOnly}>
            <DuaLajur
                rel={
                    <Kad
                        tajuk={t('prostudio.hook.groupsTitle')}
                        perihal={t('prostudio.hook.groupsDesc')}
                        kanan={<Badge tone={penuhGroup ? 'warn' : 'neutral'}>{groups.length}/{MAX_GROUP}</Badge>}
                        kaki={
                            <Btn variant="ghost" size="sm" disabled={penuhGroup} onClick={groupBaharu}
                                icon={<Plus size={14} strokeWidth={2} />} style={{ width: '100%' }}>
                                {penuhGroup ? t('prostudio.slotFull') : t('prostudio.hook.newGroup')}
                            </Btn>
                        }
                    >
                        <div style={{ display: 'grid', gap: 6 }}>
                            {groups.map((g) => {
                                const aktif = pilihGroup === g.id;
                                const isDefault = defaultGroup === g.id;
                                return (
                                    <div
                                        key={g.id}
                                        onClick={() => setPilihGroup(g.id)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            padding: '8px 10px', borderRadius: 9, cursor: 'pointer',
                                            background: aktif ? 'var(--s-fill)' : 'transparent',
                                            border: `1px solid ${aktif ? 'var(--s-line-strong)' : 'transparent'}`,
                                        }}
                                    >
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setDefaultGroup(isDefault ? '' : g.id); }}
                                            title={isDefault ? t('prostudio.hook.isDefaultGroup') : t('prostudio.hook.makeDefaultGroup')}
                                            style={{
                                                display: 'flex', padding: 0, background: 'none', border: 0,
                                                cursor: 'pointer', color: isDefault ? 'var(--s-ink)' : 'var(--s-ink-3)',
                                            }}
                                        >
                                            <Star size={15} strokeWidth={1.9} fill={isDefault ? 'currentColor' : 'none'} />
                                        </button>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontSize: 13.5, fontWeight: 500, color: 'var(--s-ink)',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>{g.nama}</div>
                                            <div style={{ fontSize: 11.5, color: 'var(--s-ink-3)' }}>
                                                {t('prostudio.hook.groupMeta', {
                                                    n: g.hooks.length,
                                                    mod: t(g.guna === 'adapt' ? 'prostudio.hook.modeAdapt' : 'prostudio.hook.modeVerbatim'),
                                                })}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); padamGroup(g.id); }}
                                            title={t('prostudio.hook.deleteGroup')}
                                            style={{
                                                display: 'flex', padding: 2, background: 'none', border: 0,
                                                cursor: 'pointer', color: 'var(--s-ink-3)',
                                            }}
                                        >
                                            <Trash2 size={14} strokeWidth={1.9} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </Kad>
                }
                isi={
                    group ? (
                        <>
                            <Kad
                                tajuk={t('prostudio.hook.settingsTitle')}
                                perihal={t('prostudio.hook.settingsDesc')}
                                kaki={
                                    <>
                                        <span className="s-dim" style={{ flex: 1 }}>
                                            {t('prostudio.hook.countInGroup', { n: group.hooks.length })}
                                        </span>
                                        <Btn onClick={simpanHook}
                                            icon={hookKilat ? <Check size={14} strokeWidth={2} /> : null}>
                                            {hookKilat ? t('prostudio.saved') : t('common.save')}
                                        </Btn>
                                    </>
                                }
                            >
                                <div style={{ display: 'grid', gap: 14 }}>
                                    <div>
                                        <Lbl>{t('prostudio.hook.groupName')}</Lbl>
                                        <Inp value={group.nama} onChange={(e) => tampalGroup({ nama: e.target.value })} />
                                    </div>

                                    <div>
                                        <Lbl>{t('prostudio.hook.howToUse')}</Lbl>
                                        <Segmented
                                            value={group.guna}
                                            onChange={(v) => tampalGroup({ guna: v })}
                                            options={[
                                                { value: 'adapt', label: t(GUNA.adapt.label) },
                                                { value: 'verbatim', label: t(GUNA.verbatim.label) },
                                            ]}
                                        />
                                        <p className="s-dim" style={{ marginTop: 8, lineHeight: 1.55 }}>
                                            {t(GUNA[group.guna].ringkas)} {t(GUNA[group.guna].untuk)}
                                        </p>
                                    </div>

                                    {/* Nota gaya hanya bermakna pada mod "AI sesuaikan" —
                                        pada mod guna-terus tiada apa yang AI boleh ikut,
                                        jadi medan itu disorok, bukan dikelabukan. */}
                                    {group.guna === 'adapt' && (
                                        <div>
                                            <Lbl>{t('prostudio.hook.styleNote')}</Lbl>
                                            <Ta
                                                value={group.nota}
                                                onChange={(e) => tampalGroup({ nota: e.target.value })}
                                                placeholder={t('prostudio.hook.styleNotePlaceholder')}
                                            />
                                        </div>
                                    )}
                                </div>
                            </Kad>

                            <Kad
                                tajuk={t('prostudio.hook.examplesTitle')}
                                perihal={t('prostudio.hook.examplesDesc')}
                                kanan={
                                    <Btn variant="quiet" size="sm" onClick={() => setPukalBuka((v) => !v)}>
                                        {pukalBuka ? t('prostudio.hook.bulkClose') : t('prostudio.hook.bulkOpen')}
                                    </Btn>
                                }
                                kaki={
                                    <Btn variant="ghost" size="sm" onClick={tambahHook}
                                        icon={<Plus size={14} strokeWidth={2} />}>
                                        {t('prostudio.hook.addHook')}
                                    </Btn>
                                }
                            >
                                <div style={{ display: 'grid', gap: 10 }}>
                                    {pukalBuka && (
                                        <div style={{
                                            display: 'grid', gap: 8, padding: 12, borderRadius: 8,
                                            background: 'var(--s-fill)', border: '1px dashed var(--s-line-strong)',
                                        }}>
                                            <Ta
                                                value={pukalTeks}
                                                onChange={(e) => setPukalTeks(e.target.value)}
                                                placeholder={t('prostudio.hook.bulkPlaceholder')}
                                                style={{ minHeight: 92 }}
                                            />
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <Btn size="sm" onClick={tambahPukal}>{t('prostudio.hook.bulkAdd')}</Btn>
                                                <Btn variant="quiet" size="sm" onClick={() => { setPukalTeks(''); setPukalBuka(false); }}>
                                                    {t('common.cancel')}
                                                </Btn>
                                            </div>
                                        </div>
                                    )}

                                    {group.hooks.length === 0 && !pukalBuka && (
                                        <span className="s-dim">{t('prostudio.hook.noHooks')}</span>
                                    )}

                                    {group.hooks.map((h, i) => (
                                        <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span className="s-num" style={{ width: 18, flex: 'none', textAlign: 'right' }}>{i + 1}</span>
                                            <Inp
                                                value={h.teks}
                                                onChange={(e) => tukarHook(h.id, e.target.value)}
                                                placeholder={t('prostudio.hook.hookPlaceholder')}
                                            />
                                            <IconBtn label={t('prostudio.hook.removeHook')} icon={<X size={14} strokeWidth={2} />}
                                                onClick={() => buangHook(h.id)} />
                                        </div>
                                    ))}
                                </div>
                            </Kad>
                        </>
                    ) : (
                        <EmptyState title={t('prostudio.hook.emptyTitle')}>
                            {t('prostudio.hook.emptyBody')}
                        </EmptyState>
                    )
                }
            />
            </Pagar>
        </>
    );

    /* ============================================================
       TINGKAT 2 — AGENT SAYA
       ============================================================
       Mod SEBENAR  → ProStudioAgentSection (logik penuh, rupa lama
                      yang sudah dipetakan ke token v3.9).
       Mod PELAN    → ProStudioAgentStepperV39 — lakaran A, stepper
                      lima langkah dengan data contoh.

       Rangka statik empat kad yang dahulu di sini DIGANTIKAN oleh
       stepper itu (arahan user 2026-08-16). Ia rangka mati; stepper
       boleh diklik, jadi susunannya benar-benar boleh dinilai. */

    const halamanAgent = (
        <>
            <KepalaFokus
                tajuk={t('prostudio.section.agent.label')}
                perihal={t('prostudio.section.agent.desc')}
                pautanKembali={sifatPautan(null)}
                /* ⛔ Mod sebenar: TIADA lencana "Ready" di kepala. Bar Simpan
                   dalam seksyen sudah memapar status SEBENAR setiap agent
                   (READY / NOT CONFIGURED) tepat di sebelah butang Simpan, dan
                   ia flip bila pelanggan mengedit. Lencana kedua di kepala hanya
                   berganda — lebih buruk, ia degil "Ready" sedangkan bar bawah
                   sudah "NOT CONFIGURED", jadi satu skrin bercakap dua benda
                   (aduan user 2026-08-20). Lencana "Contoh" kekal untuk mod
                   pelan sahaja — di sana tiada bar Simpan sebenar untuk berganda. */
                aksi={!modSebenar && <Badge tone="neutral">{t('prostudio.agent.mockBadge')}</Badge>}
            />

            {/* Mod sebenar memanggil seksyen ASAL — ia yang benar-benar
                menyimpan agent ke Firestore dan bercakap dengan extension.
                Rupanya masih rupa lama (kad putih) di dalam halaman v3.9;
                itu disengajakan sehingga seksyen itu direvamp. Corak sama
                dengan seksyen belum-revamp dalam DashboardV39View. */}
            {modSebenar ? (
                /* ⛔ TIADA pagar baca-sahaja di sini.

                   Pagar melumpuhkan SETIAP butang di dalamnya — termasuk
                   butang "Lihat pakej" pada skrin berkunci itu sendiri.
                   Hasilnya butang mati: pelanggan menekan, tiada apa berlaku
                   (aduan user 2026-08-17). Itu tepat jenis kawalan palsu yang
                   dibuang daripada halaman Settings pada 10 Ogos.

                   Ia juga tidak diperlukan: tiada akses → seksyen memapar
                   skrin berkunci sahaja, tiada borang untuk dilumpuhkan.
                   Ada akses → pelanggan memang sepatutnya boleh mengedit. */
                <ProStudioAgentSection
                    v39
                    aksesSmart={aksesSmart}
                    onNaikTaraf={onNaikTaraf}
                    agentId={sub}
                    onAlamatAgent={(id) => navigate(`${asas}/agent/${id}`, { replace: true })}
                />
            ) : (
                <ProStudioAgentStepperV39 agentId={sub} asas={`${asas}/agent`} />
            )}
        </>
    );

    /* ============================================================
       TINGKAT 2 — SEKSYEN BELUM DIBINA
       ============================================================ */
    const halamanSoon = (s) => (
        <>
            <KepalaFokus
                tajuk={t(s.label)}
                perihal={t(s.perihal)}
                pautanKembali={sifatPautan(null)}
                aksi={<Badge tone="neutral">{t('prostudio.soon')}</Badge>}
            />
            <EmptyState
                title={t('prostudio.soonTitle')}
                action={<Btn variant="ghost" onClick={() => pergi(null)}>{t('prostudio.backToHub')}</Btn>}
            >
                {t('prostudio.soonBody')}
            </EmptyState>
        </>
    );

    /* ============================================================
       TINGKAT 2 — CTA BEG KUNING
       ============================================================
       ⚠️ Mod PELAN (`/prostudio-v3.9`) TIDAK menulis apa-apa: senarai
       datang daripada keadaan tempatan, jadi lakaran boleh dinilai
       tanpa menyentuh akaun sesiapa. Mod SEBENAR menulis Firestore,
       dan snapshot hidup yang memaparkan hasilnya — jadi tiada
       salinan tempatan kedua yang boleh terpesong. */
    const halamanCta = (
        <>
            <KepalaFokus
                tajuk={t('prostudio.section.cta.label')}
                perihal={t('prostudio.cta.pageDesc')}
                pautanKembali={sifatPautan(null)}
                aksi={<Badge tone="neutral">{ctaSenarai.length}</Badge>}
            />
            {/* ⛔ `ctaKunci`, BUKAN `readOnly` — `readOnly` ialah kunci module
                SMART, dan CTA beg kuning ialah ciri TikTok. Sebelum 8 Sept
                CTA terikut sekali dengan kunci itu, jadi pelanggan
                Premium/Fighter tanpa SMART boleh membuka halaman ini tetapi
                setiap kotak mati. */}
            {/* Sepanduk claim = "pintu kedua". WAJIB ada: popup ditutup / "Nanti dulu"
                → popup disorok sepanjang hari itu, dan tanpa sepanduk ini halaman
                terkunci tanpa jalan untuk claim (ditemui user 26 Sept). */}
            {modSebenar && statusCta !== 'admin' && statusCta !== 'aktif' && (
                <div style={{ marginBottom: 16 }}>
                    <Nota>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <span style={{ flex: '1 1 240px' }}>
                                {statusCta === 'tamat'
                                    ? t('prostudio.ctaLock.tamat')
                                    : t('prostudio.ctaLock.belum')}
                            </span>
                            {statusCta === 'belum' && (
                                <Btn variant="primary" size="sm" onClick={() => setPopupCiri(CIRI.CTA)}>{t('ciri.activate')}</Btn>
                            )}
                        </span>
                    </Nota>
                </div>
            )}
            <Pagar kunci={ctaKunci}>
                <ProStudioCtaSection
                    senarai={ctaSenarai}
                    sudahMuat={ctaSudahMuat}
                    readOnly={ctaKunci}
                    onSimpan={ctaSimpan}
                    onPadam={ctaPadam}
                    t={t}
                />
            </Pagar>
        </>
    );

    /* ============================================================
       TINGKAT 2 — SHORTCUT "/"
       ============================================================ */
    const halamanShortcut = (
        <>
            <KepalaFokus
                tajuk={t('prostudio.section.shortcut.label')}
                perihal={t('prostudio.sc.pageDesc')}
                pautanKembali={sifatPautan(null)}
                aksi={<Badge tone="neutral">{scSenarai.length}</Badge>}
            />
            <ProStudioShortcutSection
                senarai={scSenarai}
                sudahMuat={modSebenar ? scSebenar.sudahMuat : true}
                ralatMuat={modSebenar ? scSebenar.ralat : ''}
                status={statusSc}
                onSimpan={scSimpan}
                onPadam={scPadam}
                onAktifkan={() => setPopupCiri(CIRI.SHORTCUT)}
                t={t}
            />
        </>
    );

    /* ── PROTOTAIP: Blocks (module GOYANG) ───────────────────────
       Seksyen ini tidak wujud dalam mod sebenar — lihat `seksyenPapar`. */
    const halamanBlok = (
        <>
            <KepalaFokus
                tajuk={t('prostudio.section.blok.label')}
                perihal={t('prostudio.section.blok.desc')}
                pautanKembali={sifatPautan(null)}
                aksi={<Badge tone="neutral">GOYANG</Badge>}
            />
            <ProStudioBlokSection />
        </>
    );

    /* ── Pemilih paparan ─────────────────────────────────────────
       `semasa` dikira di atas daripada alamat, bukan di sini. */
    let isi = hub;
    if (seksyen === 'gaya') isi = halamanGaya;
    else if (seksyen === 'hook') isi = halamanHook;
    else if (seksyen === 'agent') isi = halamanAgent;
    else if (seksyen === 'cta') isi = halamanCta;
    else if (seksyen === 'shortcut') isi = halamanShortcut;
    else if (seksyen === 'blok') isi = halamanBlok;
    else if (semasa) isi = halamanSoon(semasa);

    /* Panel dan bingkai dipasang oleh StudioShell — halaman ini hanya
       menyumbang isinya, sama seperti halaman -v3.9 yang lain. */
    return (
        <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>
            {isi}
            {!modSebenar && <p className="s-dim" style={{ marginTop: 26 }}>{t('prostudio.planNote')}</p>}
            {popupPapar && <PopupCiriPercuma ciri={popupPapar} onTutup={tutupPopup} t={t} />}
        </div>
    );
}
